import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function ResetPasswordForm() {
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [passwordHelpText, setPasswordHelpText] = useState('');
    const navigate = useNavigate();

    // Check if there's a hash in the URL which contains the reset token
    useEffect(() => {
        const hash = window.location.hash;
        if (!hash || !hash.includes('access_token') || !hash.includes('type=recovery')) {
            setError('Invalid or expired password reset link. Please request a new one.');
        }
    }, []);

    // Password validation function
    const validatePassword = (password) => {
        const hasLowercase = /[a-z]/.test(password);
        const hasUppercase = /[A-Z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password);
        const isLongEnough = password.length >= 8;
        
        const requirements = [];
        
        if (!hasLowercase) requirements.push('lowercase letter');
        if (!hasUppercase) requirements.push('uppercase letter');
        if (!hasNumber) requirements.push('number');
        if (!hasSpecial) requirements.push('special character');
        if (!isLongEnough) requirements.push('minimum 8 characters');
        
        return {
            isValid: requirements.length === 0,
            requirements
        };
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        
        // Clear errors when user types
        if (e.target.name === 'password' || e.target.name === 'confirmPassword') {
            setError('');
            setPasswordHelpText('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setPasswordHelpText('');
        
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        
        // Validate password strength
        const validation = validatePassword(formData.password);
        if (!validation.isValid) {
            const missingRequirements = validation.requirements.join(', ');
            setPasswordHelpText(`Password must contain: ${missingRequirements}`);
            setError(`Password too weak. Please include ${missingRequirements}.`);
            return;
        }
        
        setLoading(true);
        
        try {
            if (!window.supabaseClient) {
                throw new Error('Authentication service is not available');
            }
            
            const { error } = await window.supabaseClient.auth.updateUser({
                password: formData.password
            });
            
            if (error) throw error;
            
            setSuccess(true);
            
            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
            
        } catch (err) {
            console.error('Password reset error:', err);
            setError(err.message || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
                <div className="text-center">
                    <div className="text-green-500 text-5xl mb-4">
                        <i className="fas fa-check-circle"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Password Reset Successful</h2>
                    <p className="text-gray-600 mb-6">
                        Your password has been successfully reset. You will be redirected to the login page.
                    </p>
                    <button 
                        onClick={() => navigate('/login')}
                        className="btn btn-primary"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
            <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">Reset Password</h2>
            
            {error && (
                <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-center">
                    <i className="fas fa-exclamation-circle mr-2"></i>
                    <span>{error}</span>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label htmlFor="password" className="block text-gray-700 mb-2 font-medium">
                        New Password <span className="text-red-500">*</span>
                    </label>
                    <input
                        id="password"
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                        minLength="8"
                        autoComplete="new-password"
                        style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                    />
                    {passwordHelpText && (
                        <p className="mt-2 text-sm text-gray-600">
                            {passwordHelpText}
                        </p>
                    )}
                </div>

                <div>
                    <label htmlFor="confirmPassword" className="block text-gray-700 mb-2 font-medium">
                        Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <input
                        id="confirmPassword"
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                        minLength="8"
                        autoComplete="new-password"
                        style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                    />
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary w-full"
                >
                    {loading ? (
                        <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            Resetting Password...
                        </>
                    ) : (
                        'Reset Password'
                    )}
                </button>
            </form>
        </div>
    );
}

export default ResetPasswordForm;
